import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { CompanyHeader } from "@/components/admin/CompanyHeader";
import { DashboardTabs } from "@/components/admin/dashboard/DashboardTabs";
import { DashboardSidebar } from "@/components/admin/DashboardSidebar";
import { TeamInviteAlert } from "@/components/admin/TeamInviteAlert";
import { OnboardingFlow } from "@/components/admin/OnboardingFlow";
import { toast } from "sonner";
import { useCompanyData } from "@/hooks/useCompanyData";
import { useFeedbackData } from "@/hooks/useFeedbackData";
import { GoogleCompanyPrompt } from "@/components/auth/GoogleCompanyPrompt";
import { useTeamInvitation } from "@/hooks/auth/useTeamInvitation";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState<any>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showCompanyPrompt, setShowCompanyPrompt] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    // Remove any 'dark' class to ensure we only use admin-dark for admin pages
    document.documentElement.classList.remove('dark');

    // Apply dark mode immediately on mount (default theme)
    const savedTheme = localStorage.getItem("admin-theme");
    if (!savedTheme || savedTheme === "dark") {
      document.documentElement.classList.add("admin-dark");
    }

    if (searchParams.get('upgraded') === 'true') {
      toast.success('Successfully upgraded to Pro plan!');
    }
  }, [searchParams]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate('/auth');
          return;
        }
        setUser(session.user);
      } catch (error) {
        console.error("Error initializing auth:", error);
        navigate('/auth');
      } finally {
        setInitialLoading(false);
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser(session.user);
      } else {
        navigate('/auth');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  const { company, isLoading: isLoadingCompany, refetchCompany } = useCompanyData(user?.id);
  const { feedback = [], refetchFeedback, companyStatuses } = useFeedbackData(company?.id);
  const [checkingInvitations, setCheckingInvitations] = useState(true);
  const [userName, setUserName] = useState<string | null>(null);

  // Check for pending team invitations
  useEffect(() => {
    const checkForInvitations = async () => {
      if (!user?.id) {
        setCheckingInvitations(false);
        return;
      }

      try {
        // Check for any pending team invitations for this user's email
        const { data: invitations, error } = await supabase
          .from('team_members')
          .select('id, email, company_id, status')
          .eq('email', user.email?.toLowerCase())
          .eq('status', 'pending');

        if (error) throw error;

        // If we have pending invitations, try to accept the first one
        if (invitations && invitations.length > 0) {
          const invitation = invitations[0];
          const { error: acceptError } = await supabase.rpc('accept_team_invitation', {
            p_user_id: user.id,
            p_team_member_id: invitation.id
          });

          if (!acceptError) {
            // Refresh company data
            await refetchCompany();
            toast.success('Successfully joined the team!');
            return;
          }
        }
      } catch (error) {
        console.error('Error checking team invitations:', error);
      } finally {
        setCheckingInvitations(false);
      }
    };

    if (user?.email && !company) {
      checkForInvitations();
    } else {
      setCheckingInvitations(false);
    }
  }, [user, company, refetchCompany]);

  // Show company creation prompt if no company and done checking invitations
  useEffect(() => {
    if (!isLoadingCompany && !company && user && !checkingInvitations) {
      setShowCompanyPrompt(true);
    }
  }, [isLoadingCompany, company, user, checkingInvitations]);

  // Check if we should show onboarding
  useEffect(() => {
    if (company && company.onboarding_completed === false) {
      setShowOnboarding(true);
    }
  }, [company]);

  // Fetch current user's name from team_members
  useEffect(() => {
    const fetchUserName = async () => {
      if (!user?.id || !company?.id) return;

      try {
        const { data, error } = await supabase
          .from('team_members')
          .select('name')
          .eq('company_id', company.id)
          .eq('user_id', user.id)
          .maybeSingle();

        if (!error && data?.name) {
          setUserName(data.name);
        }
      } catch (error) {
        console.error('Error fetching user name:', error);
      }
    };

    fetchUserName();
  }, [user?.id, company?.id]);

  const submitCompanyName = async () => {
    const trimmedCompanyName = companyName.trim();
    
    if (!trimmedCompanyName) {
      toast.error("Please enter a company name");
      return;
    }

    try {
      setLoading(true);
      
      const slug = trimmedCompanyName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      
      const { data: company, error: companyError } = await supabase
        .from("companies")
        .insert({
          name: trimmedCompanyName,
          slug: slug,
          owner_id: user.id
        })
        .select()
        .single();

      if (companyError) {
        throw companyError;
      }
      
      const { error: profileError } = await supabase
        .from("profiles")
        .update({ company_id: company.id })
        .eq("id", user.id);

      if (profileError) {
        throw profileError;
      }

      // Check for Whop license and update company plan to enterprise if found
      const whopLicenseKey = localStorage.getItem('whop_license_key');
      const whopMembershipData = localStorage.getItem('whop_membership_data');

      if (whopLicenseKey && whopMembershipData) {
        try {
          console.log("Applying Whop license to company...");
          const membershipData = JSON.parse(whopMembershipData);

          // Update company to enterprise plan with license info
          const { error: updateCompanyError } = await supabase
            .from("companies")
            .update({
              plan: 'enterprise',
              whop_license_key: whopLicenseKey,
              whop_membership_id: membershipData.id,
              whop_validated_at: new Date().toISOString(),
              whop_metadata: membershipData,
            })
            .eq("id", company.id);

          if (updateCompanyError) {
            console.error("Error updating company with license:", updateCompanyError);
            toast.error("Failed to apply license to company");
          } else {
            console.log("Company updated with Whop license successfully");

            // Bind the license with metadata
            const { error: bindError } = await supabase.functions.invoke('validate-whop-license', {
              body: {
                licenseKey: whopLicenseKey,
                companyId: company.id,
                preSignupValidation: false,
              },
            });

            if (bindError) {
              console.error("Error binding license:", bindError);
            } else {
              console.log("License bound to company successfully");
            }

            // Clean up localStorage
            localStorage.removeItem('whop_license_key');
            localStorage.removeItem('whop_membership_data');

            toast.success("Enterprise license activated!");
          }
        } catch (error) {
          console.error("Error processing Whop license:", error);
        }
      }

      // Create default statuses for the company (same as email/password flow)
      const defaultStatuses = [
        { name: "In Review", color: "#94a3b8", description: "", position: 0 },
        { name: "Planned", color: "#60a5fa", description: "", position: 1 },
        { name: "In Progress", color: "#f59e0b", description: "", position: 2 },
        { name: "Completed", color: "#10b981", description: "", position: 3 },
        { name: "Rejected", color: "#ef4444", description: "", position: 4 },
      ];

      const { error: statusError } = await supabase
        .from("company_statuses")
        .insert(
          defaultStatuses.map((status) => ({
            ...status,
            company_id: company.id,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            position: status.position,
          }))
        );

      if (statusError) {
        console.error("Error creating default statuses:", statusError);
      } else {
        console.log("Default statuses created successfully");
      }

      // Add owner as a team member
      const { data: existingMember, error: lookupError } = await supabase
        .from("team_members")
        .select("id, user_id")
        .eq("company_id", company.id)
        .or(`user_id.eq.${user.id},email.eq.${user.email?.toLowerCase()}`)
        .maybeSingle();

      if (lookupError) {
        console.error("Error checking for existing team member:", lookupError);
      } else if (existingMember) {
        // If a team member record exists but doesn't have user_id, update it
        if (!existingMember.user_id) {
          console.log("Updating existing team member record with user_id");
          const { error: updateError } = await supabase
            .from("team_members")
            .update({
              user_id: user.id,
              name: user.email?.split("@")[0] || "Team Member",
              status: 'active',
              updated_at: new Date().toISOString()
            })
            .eq("id", existingMember.id);

          if (updateError) {
            console.error("Error updating team member:", updateError);
          } else {
            console.log("Team member record updated successfully");
          }
        } else {
          console.log("User is already a team member, skipping duplicate entry");
        }
      } else {
        // No existing record, create a new one (owner scenario)
        const { error: teamMemberError } = await supabase
          .from("team_members")
          .insert({
            company_id: company.id,
            email: user.email?.toLowerCase(),
            name: user.email?.split("@")[0] || "Team Member",
            user_id: user.id,
            status: 'active',
            invited_by: user.id,
            created_at: new Date().toISOString()
          });

        if (teamMemberError) {
          console.error("Error adding user to team members:", teamMemberError);
        } else {
          console.log("User added to team members successfully");
        }
      }

      toast.success("Company created successfully!");
      setShowCompanyPrompt(false);
      await refetchCompany();
    } catch (error: any) {
      console.error("Error creating company:", error);
      toast.error(error.message || "Failed to create company");
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading || isLoadingCompany || checkingInvitations) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (showCompanyPrompt) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <GoogleCompanyPrompt
          companyName={companyName}
          setCompanyName={setCompanyName}
          onSubmit={submitCompanyName}
          loading={loading}
        />
      </div>
    );
  }

  if (!company) {
    return null;
  }

  const feedbackCount = feedback.length;
  const feedbackLimit = company.plan === 'free' ? 10 : Infinity;
  const isAtLimit = feedbackCount >= feedbackLimit;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {user && company && (
        <TeamInviteAlert
          userId={user.id}
          currentCompanyId={company.id}
        />
      )}

      {showOnboarding && company && (
        <OnboardingFlow
          companyId={company.id}
          companyName={company.name}
          onComplete={async () => {
            setShowOnboarding(false);
            await refetchCompany();
            await refetchFeedback();
          }}
        />
      )}

      <CompanyHeader
        company={company}
        feedbackCount={feedbackCount}
        feedbackLimit={feedbackLimit}
        feedback={feedback}
        onCompanyUpdate={refetchCompany}
      />

      <div className="flex flex-1 overflow-hidden">
        <DashboardSidebar
          userEmail={user?.email}
          userName={userName || undefined}
        />

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto p-6">
            <DashboardTabs
              company={company}
              feedback={feedback}
              isAtLimit={isAtLimit}
              onFeedbackUpdate={refetchFeedback}
              onCompanyUpdate={refetchCompany}
              currentUserId={user?.id}
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
